/**
 * UserPreferenceEngine — Client-side personalization via localStorage
 *
 * Tracks user behavior (searches, views, likes, clicks) and calculates
 * category affinities, location preferences, and recommendations.
 *
 * TODO-BACKEND: reemplazar con endpoint /api/recommendations cuando esté listo
 * Solución temporal: localStorage para personalización sin backend
 */

const STORAGE_KEY = 'pm_user_preferences';
const MAX_RECENT_SEARCHES = 20;
const MAX_RECENT_VIEWS = 50;
const MAX_CATEGORY_HISTORY = 100;
const AFFINITY_DECAY_DAYS = 30; // Older interactions count less

// ── Supabase sync config ──
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;
const SYNC_TABLE = 'user_preferences';
const SB_HEADERS = {
  'apikey': SUPABASE_ANON_KEY,
  'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
  'Content-Type': 'application/json',
  'Prefer': 'resolution=merge-duplicates,return=representation',
};
const SYNC_DEBOUNCE_MS = 5000; // Sync to Supabase 5s after last change
let syncTimer = null;

const getUserId = () => {
  try {
    const raw = localStorage.getItem('currentUser');
    if (!raw) return null;
    const user = JSON.parse(raw);
    return user?.id ? String(user.id) : null;
  } catch { return null; }
};

// ── Helpers ──

const now = () => Date.now();

const daysSince = (timestamp) => (now() - timestamp) / (1000 * 60 * 60 * 24);

/** Weight decays over time: recent = 1.0, 30 days ago = ~0.37 */
const decayWeight = (timestamp) => Math.exp(-daysSince(timestamp) / AFFINITY_DECAY_DAYS);

const safeGet = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const safeSave = (data) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    try {
      const trimmed = { ...data };
      trimmed.recentSearches = (trimmed.recentSearches || []).slice(0, 10);
      trimmed.recentViews = (trimmed.recentViews || []).slice(0, 20);
      trimmed.categoryInteractions = (trimmed.categoryInteractions || []).slice(0, 50);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
    } catch {
      // Silently fail
    }
  }

  // Debounced sync to Supabase (only if logged in)
  if (syncTimer) clearTimeout(syncTimer);
  syncTimer = setTimeout(() => {
    syncToCloud().catch(() => {});
  }, SYNC_DEBOUNCE_MS);
};

// ── Cloud Sync (Supabase) ──

/** Push local state to Supabase */
const syncToCloud = async () => {
  const userId = getUserId();
  if (!userId || !SUPABASE_URL) return;

  const state = safeGet();
  if (!state) return;

  try {
    await fetch(`${SUPABASE_URL}/rest/v1/${SYNC_TABLE}`, {
      method: 'POST',
      headers: SB_HEADERS,
      body: JSON.stringify({
        user_id: userId,
        preferences: state,
        updated_at: new Date().toISOString(),
      }),
    });
  } catch {
    // Silently fail — localStorage is the primary source
  }
};

/** Pull from Supabase and merge with local state */
const syncFromCloud = async () => {
  const userId = getUserId();
  if (!userId || !SUPABASE_URL) return;

  try {
    const res = await fetch(
      `${SUPABASE_URL}/rest/v1/${SYNC_TABLE}?user_id=eq.${userId}&select=preferences,updated_at`,
      { headers: { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${SUPABASE_ANON_KEY}` } }
    );
    if (!res.ok) return;

    const rows = await res.json();
    if (rows.length === 0) return;

    const cloudState = rows[0].preferences;
    if (!cloudState) return;

    const localState = getState();

    // Merge: combine arrays, deduplicate by unique key, keep most recent
    const merged = mergeStates(localState, cloudState);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
  } catch {
    // Silently fail — localStorage works offline
  }
};

/** Merge two preference states, keeping unique entries from both */
const mergeStates = (local, cloud) => {
  const merged = { ...defaultState() };

  // Merge recent searches — dedupe by query, keep most recent timestamp
  const searchMap = new Map();
  for (const s of [...(cloud.recentSearches || []), ...(local.recentSearches || [])]) {
    const key = s.query?.toLowerCase();
    if (!key) continue;
    const existing = searchMap.get(key);
    if (!existing || s.timestamp > existing.timestamp) searchMap.set(key, s);
  }
  merged.recentSearches = [...searchMap.values()]
    .sort((a, b) => b.timestamp - a.timestamp)
    .slice(0, MAX_RECENT_SEARCHES);

  // Merge recent views — dedupe by id, keep most recent
  const viewMap = new Map();
  for (const v of [...(cloud.recentViews || []), ...(local.recentViews || [])]) {
    if (!v.id) continue;
    const existing = viewMap.get(v.id);
    if (!existing || v.timestamp > existing.timestamp) viewMap.set(v.id, v);
  }
  merged.recentViews = [...viewMap.values()]
    .sort((a, b) => b.timestamp - a.timestamp)
    .slice(0, MAX_RECENT_VIEWS);

  // Merge category interactions — just concatenate and trim
  merged.categoryInteractions = [
    ...(local.categoryInteractions || []),
    ...(cloud.categoryInteractions || [])
  ].sort((a, b) => b.timestamp - a.timestamp)
    .slice(0, MAX_CATEGORY_HISTORY);

  // Merge location interactions
  merged.locationInteractions = [
    ...(local.locationInteractions || []),
    ...(cloud.locationInteractions || [])
  ].sort((a, b) => b.timestamp - a.timestamp)
    .slice(0, 50);

  // Merge contacted
  const contactMap = new Map();
  for (const c of [...(cloud.contacted || []), ...(local.contacted || [])]) {
    const key = `${c.id}-${c.method}-${c.timestamp}`;
    contactMap.set(key, c);
  }
  merged.contacted = [...contactMap.values()]
    .sort((a, b) => b.timestamp - a.timestamp)
    .slice(0, 50);

  // Merge forum keywords
  merged.forumKeywords = [
    ...(local.forumKeywords || []),
    ...(cloud.forumKeywords || [])
  ].sort((a, b) => b.timestamp - a.timestamp)
    .slice(0, 50);

  merged.version = 1;
  return merged;
};

// ── Default State ──

const defaultState = () => ({
  // Search history: [{ query, tab, filters, timestamp }]
  recentSearches: [],
  // Viewed businesses: [{ id, name, category, location, logo, rating, timestamp }]
  recentViews: [],
  // Category interactions: [{ category, action, timestamp, weight }]
  // action: 'search' | 'view' | 'like' | 'contact' | 'click'
  categoryInteractions: [],
  // Location interactions: [{ location, timestamp }]
  locationInteractions: [],
  // Contacted businesses: [{ id, method, timestamp }]
  contacted: [],
  // Computed affinities (recalculated periodically)
  affinities: {
    categories: {},  // { "Equipos": 0.85, "Repuestos": 0.62, ... }
    locations: {},   // { "Santiago": 0.9, "Antofagasta": 0.5, ... }
    lastComputed: 0
  },
  // Version for migrations
  version: 1
});

// ── Core Engine ──

const getState = () => {
  const stored = safeGet();
  if (!stored) return defaultState();
  // Simple version check
  if (!stored.version || stored.version < 1) {
    return { ...defaultState(), ...stored, version: 1 };
  }
  return stored;
};

// ── Track Actions ──

export const preferenceEngine = {
  /**
   * Record a search query
   */
  trackSearch(query, tab = 'businesses', filters = {}) {
    if (!query || query.trim().length < 2) return;
    const state = getState();
    const search = {
      query: query.trim(),
      tab,
      filters,
      timestamp: now()
    };

    // Deduplicate: remove exact same query if recent
    state.recentSearches = state.recentSearches.filter(
      s => s.query.toLowerCase() !== query.trim().toLowerCase()
    );
    state.recentSearches.unshift(search);
    state.recentSearches = state.recentSearches.slice(0, MAX_RECENT_SEARCHES);

    // Extract category signals from filters
    if (filters.category) {
      state.categoryInteractions.push({
        category: filters.category,
        action: 'search',
        timestamp: now(),
        weight: 1
      });
    }

    // Extract location signals
    if (filters.coverage) {
      state.locationInteractions.push({
        location: filters.coverage,
        timestamp: now()
      });
    }

    safeSave(state);
  },

  /**
   * Record viewing a business profile
   */
  trackView(business) {
    if (!business?.id) return;
    const state = getState();

    const specs = Array.isArray(business.specializations) ? business.specializations : [];
    const brands = Array.isArray(business.brands) ? business.brands : [];
    const coverage = Array.isArray(business.coverage_regions) ? business.coverage_regions : [];

    const view = {
      id: business.id,
      name: business.name || business.business_name || '',
      category: business.category || business.category_name || '',
      location: business.location || business.address || '',
      logo: business.logo || business.logo_url || '',
      rating: business.rating || 0,
      verification_level: business.verification_level,
      is_featured: business.is_featured || false,
      slug: business.slug || business.alias || '',
      specializations: specs.slice(0, 10).map(s => typeof s === 'string' ? s : (s.name || s.title || '')).filter(Boolean),
      brands: brands.slice(0, 10).map(b => typeof b === 'string' ? b : (b.name || '')).filter(Boolean),
      coverage: coverage.slice(0, 5).map(c => typeof c === 'string' ? c : (c.name || '')).filter(Boolean),
      // Campos necesarios para el chip 🌎 en CarouselCard (sin ellos la
      // sección "Últimos vistos" no puede pintar cobertura ni ciudad).
      city: business.city || '',
      country: business.country || '',
      coverage_regions: business.coverage_regions || null,
      timestamp: now()
    };

    // Remove previous view of same business
    state.recentViews = state.recentViews.filter(v => v.id !== business.id);
    state.recentViews.unshift(view);
    state.recentViews = state.recentViews.slice(0, MAX_RECENT_VIEWS);

    // Category signal (viewing is stronger than searching)
    if (view.category) {
      state.categoryInteractions.push({
        category: view.category,
        action: 'view',
        timestamp: now(),
        weight: 2
      });
    }

    // Location signal
    if (view.location) {
      state.locationInteractions.push({
        location: view.location,
        timestamp: now()
      });
    }

    safeSave(state);
  },

  /**
   * Record clicking on a search result
   */
  trackClick(business, positionInResults = -1) {
    if (!business?.id) return;
    const state = getState();

    if (business.category || business.category_name) {
      state.categoryInteractions.push({
        category: business.category || business.category_name,
        action: 'click',
        timestamp: now(),
        weight: 1.5
      });
    }

    // Trim if too large
    if (state.categoryInteractions.length > MAX_CATEGORY_HISTORY) {
      state.categoryInteractions = state.categoryInteractions.slice(-MAX_CATEGORY_HISTORY);
    }

    safeSave(state);
  },

  /**
   * Record liking/favoriting a business
   */
  trackLike(business) {
    if (!business?.id) return;
    const state = getState();

    if (business.category || business.category_name) {
      state.categoryInteractions.push({
        category: business.category || business.category_name,
        action: 'like',
        timestamp: now(),
        weight: 3 // Likes are strongest signal
      });
    }

    safeSave(state);
  },

  /**
   * Record contacting a business
   */
  trackContact(businessId, method = 'phone') {
    const state = getState();

    state.contacted.push({
      id: businessId,
      method,
      timestamp: now()
    });

    // Keep last 50 contacts
    state.contacted = state.contacted.slice(-50);
    safeSave(state);
  },

  // ── Read Data ──

  /**
   * Get recent searches (most recent first)
   */
  getRecentSearches(limit = 8) {
    const state = getState();
    return state.recentSearches.slice(0, limit);
  },

  /**
   * Get recently viewed businesses (most recent first)
   */
  getRecentViews(limit = 10) {
    const state = getState();
    return state.recentViews.slice(0, limit);
  },

  /**
   * Calculate category affinities from interaction history
   * Returns sorted array: [{ category, score }]
   */
  getCategoryAffinities() {
    const state = getState();
    const scores = {};

    for (const interaction of state.categoryInteractions) {
      if (!interaction.category) continue;
      const cat = interaction.category;
      const decay = decayWeight(interaction.timestamp);
      const weight = interaction.weight || 1;
      scores[cat] = (scores[cat] || 0) + (weight * decay);
    }

    return Object.entries(scores)
      .map(([category, score]) => ({ category, score }))
      .sort((a, b) => b.score - a.score);
  },

  /**
   * Get top N preferred categories
   */
  getTopCategories(limit = 5) {
    return this.getCategoryAffinities()
      .slice(0, limit)
      .map(a => a.category);
  },

  /**
   * Get preferred locations based on search/view history
   */
  getPreferredLocations(limit = 3) {
    const state = getState();
    const scores = {};

    for (const loc of state.locationInteractions) {
      if (!loc.location) continue;
      const decay = decayWeight(loc.timestamp);
      scores[loc.location] = (scores[loc.location] || 0) + decay;
    }

    return Object.entries(scores)
      .sort(([, a], [, b]) => b - a)
      .slice(0, limit)
      .map(([location]) => location);
  },

  /**
   * Get search suggestions based on user's history and patterns
   */
  getPersonalizedSuggestions(currentQuery = '') {
    const state = getState();
    const suggestions = [];

    // Recent searches that match current input
    if (currentQuery.length > 0) {
      const lower = currentQuery.toLowerCase();
      const matching = state.recentSearches
        .filter(s => s.query.toLowerCase().includes(lower))
        .map(s => ({ type: 'recent', query: s.query, tab: s.tab }));
      suggestions.push(...matching.slice(0, 3));
    }

    // Top category suggestions
    const topCats = this.getTopCategories(3);
    for (const cat of topCats) {
      suggestions.push({ type: 'category', query: cat, tab: 'businesses' });
    }

    return suggestions;
  },

  /**
   * Check if user has enough data for personalization
   */
  hasPersonalizationData() {
    const state = getState();
    return (
      state.recentSearches.length >= 1 ||
      state.recentViews.length >= 1 ||
      state.categoryInteractions.length >= 1
    );
  },

  /**
   * Get a "discovery profile" summary for building recommendation queries
   */
  getDiscoveryProfile() {
    const topCategories = this.getTopCategories(5);
    const preferredLocations = this.getPreferredLocations(3);
    const recentViews = this.getRecentViews(10);
    const recentSearches = this.getRecentSearches(5);

    return {
      topCategories,
      preferredLocations,
      recentViewIds: recentViews.map(v => v.id),
      recentSearchTerms: recentSearches.map(s => s.query),
      hasData: this.hasPersonalizationData(),
      viewCount: recentViews.length,
      searchCount: recentSearches.length
    };
  },

  /**
   * Clear all preference data
   */
  clear() {
    localStorage.removeItem(STORAGE_KEY);
  },

  /**
   * Sync preferences from cloud (Supabase).
   * Call this on login or app init when user is authenticated.
   * Merges cloud data with local — never overwrites, always combines.
   */
  async pullFromCloud() {
    await syncFromCloud();
  },

  /**
   * Force push local preferences to cloud.
   * Call this before logout if needed.
   */
  async pushToCloud() {
    await syncToCloud();
  },

  // ── Smart Recommendations ──

  /**
   * Cross-category relationships map.
   * If a user is interested in category A, they might also want category B.
   * Each entry: category → [{ related, reason }]
   */
  getCrossCategories(category) {
    const CROSS_MAP = {
      'Servicios Mineros':          [{ related: 'Servicios de Mantenimiento', reason: 'Complementa tus operaciones mineras' },
                                     { related: 'Tecnología y Software', reason: 'Automatiza tus procesos' }],
      'Servicios de Mantenimiento': [{ related: 'Servicios Mineros', reason: 'Equipos que podrías necesitar' },
                                     { related: 'Energías Renovables', reason: 'Eficiencia energética para tu planta' }],
      'Energías Renovables':        [{ related: 'Tecnología y Software', reason: 'Monitoreo y control energético' },
                                     { related: 'Servicios de Mantenimiento', reason: 'Mantención de tus sistemas' }],
      'Tecnología y Software':      [{ related: 'Servicios Mineros', reason: 'Soluciones para minería' },
                                     { related: 'Energías Renovables', reason: 'Tecnología para energía' }],
      'Servicios de Ingeniería':    [{ related: 'Servicios Mineros', reason: 'Proyectos mineros integrales' },
                                     { related: 'Servicios de Mantenimiento', reason: 'Ingeniería de mantención' }],
    };

    // Try exact match first, then partial match
    if (CROSS_MAP[category]) return CROSS_MAP[category];

    for (const [key, value] of Object.entries(CROSS_MAP)) {
      if (category.toLowerCase().includes(key.toLowerCase()) ||
          key.toLowerCase().includes(category.toLowerCase())) {
        return value;
      }
    }
    return [];
  },

  /**
   * Brand affinities — devuelve [{brand, score}] ordenado desc. score con
   * decay temporal (mismo criterio que getCategoryAffinities). Sirve para
   * que usePersonalization decida qué marcas merecen sección propia
   * aplicando un umbral.
   */
  getBrandAffinities() {
    const state = getState();
    const scores = {};
    for (const view of state.recentViews) {
      for (const brand of (view.brands || [])) {
        scores[brand] = (scores[brand] || 0) + decayWeight(view.timestamp);
      }
    }
    return Object.entries(scores)
      .map(([brand, score]) => ({ brand, score }))
      .sort((a, b) => b.score - a.score);
  },

  /**
   * Specialization affinities — devuelve [{spec, score}] ordenado desc.
   * Mismo decay temporal que getCategoryAffinities/getBrandAffinities.
   */
  getSpecAffinities() {
    const state = getState();
    const scores = {};
    for (const view of state.recentViews) {
      for (const spec of (view.specializations || [])) {
        scores[spec] = (scores[spec] || 0) + decayWeight(view.timestamp);
      }
    }
    return Object.entries(scores)
      .map(([spec, score]) => ({ spec, score }))
      .sort((a, b) => b.score - a.score);
  },

  /**
   * Get brands the user has shown interest in (from viewed businesses)
   */
  getInterestedBrands(limit = 5) {
    const state = getState();
    const brandCounts = {};
    for (const view of state.recentViews) {
      for (const brand of (view.brands || [])) {
        brandCounts[brand] = (brandCounts[brand] || 0) + decayWeight(view.timestamp);
      }
    }
    return Object.entries(brandCounts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, limit)
      .map(([brand]) => brand);
  },

  /**
   * Get specializations the user has shown interest in
   */
  getInterestedSpecializations(limit = 5) {
    const state = getState();
    const specCounts = {};
    for (const view of state.recentViews) {
      for (const spec of (view.specializations || [])) {
        specCounts[spec] = (specCounts[spec] || 0) + decayWeight(view.timestamp);
      }
    }
    return Object.entries(specCounts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, limit)
      .map(([spec]) => spec);
  },

  /**
   * Build smart recommendation queries based on full user profile.
   * Returns array of { id, title, subtitle, icon, searchParams }
   */
  getSmartRecommendations() {
    const recommendations = [];
    const recentViews = this.getRecentViews(10);
    const recentSearches = this.getRecentSearches(10);
    const forumKeywords = this.getForumKeywords(8);
    const usedIds = new Set(); // avoid duplicate recommendation topics

    // Need at least some activity
    if (recentViews.length === 0 && recentSearches.length === 0 && forumKeywords.length === 0) return recommendations;

    const topCategories = this.getTopCategories(3);
    const brands = this.getInterestedBrands(3);
    const specializations = this.getInterestedSpecializations(3);

    // ── Extract keywords from search queries ──
    // Split searches into meaningful terms for cross-matching
    const searchTerms = [];
    for (const s of recentSearches) {
      const words = s.query.split(/\s+/).filter(w => w.length > 3);
      searchTerms.push(...words);
    }
    // Count frequency of search terms
    const termFreq = {};
    for (const term of searchTerms) {
      const lower = term.toLowerCase();
      termFreq[lower] = (termFreq[lower] || 0) + 1;
    }
    const topSearchTerms = Object.entries(termFreq)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5)
      .map(([term]) => term);

    // 1. Cross-category: "Completa tu cadena"
    if (topCategories.length > 0) {
      const crossCats = this.getCrossCategories(topCategories[0]);
      for (const cross of crossCats.slice(0, 1)) {
        if (!topCategories.includes(cross.related)) {
          const id = `cross-${cross.related.replace(/\s+/g, '-').toLowerCase()}`;
          recommendations.push({
            id, title: cross.reason,
            subtitle: `Proveedores de ${cross.related}`,
            icon: 'GitBranch',
            searchParams: { category: cross.related, page_size: 10 }
          });
          usedIds.add(id);
        }
      }
    }

    // 2. By brand (from views + searches)
    // Also extract brand-like terms from searches (capitalized words, known patterns)
    const allBrands = [...brands];
    for (const s of recentSearches) {
      // Words that look like brand names (capitalized, or known brands)
      const words = s.query.split(/\s+/).filter(w => w.length > 2 && /^[A-Z]/.test(w));
      allBrands.push(...words);
    }
    const uniqueBrands = [...new Set(allBrands)];

    if (uniqueBrands.length > 0) {
      const topBrand = uniqueBrands[0];
      const id = `brand-${topBrand.replace(/\s+/g, '-').toLowerCase()}`;
      if (!usedIds.has(id)) {
        recommendations.push({
          id, title: `Trabajan con ${topBrand}`,
          subtitle: `Proveedores que manejan la marca ${topBrand}`,
          icon: 'Tag',
          searchParams: { search: topBrand, page_size: 10 }
        });
        usedIds.add(id);
      }
    }

    // 3. By specialization (from views)
    if (specializations.length > 0) {
      const topSpec = specializations[0];
      const id = `spec-${topSpec.replace(/\s+/g, '-').toLowerCase()}`;
      if (!usedIds.has(id)) {
        recommendations.push({
          id, title: `Especialistas en ${topSpec}`,
          subtitle: `Proveedores con esta especialización`,
          icon: 'Target',
          searchParams: { search: topSpec, page_size: 10 }
        });
        usedIds.add(id);
      }
    }

    // 4. From search patterns: "Relacionado con tus búsquedas"
    // Use top search terms that aren't already covered by brands/specs
    const coveredTerms = new Set([...uniqueBrands, ...specializations].map(t => t.toLowerCase()));
    const unusedTerms = topSearchTerms.filter(t => !coveredTerms.has(t));

    if (unusedTerms.length > 0) {
      // Combine top 2 unused terms for a richer query
      const combinedQuery = unusedTerms.slice(0, 2).join(' ');
      const id = `search-pattern-${combinedQuery.replace(/\s+/g, '-')}`;
      if (!usedIds.has(id)) {
        recommendations.push({
          id, title: `Relacionado con "${combinedQuery}"`,
          subtitle: 'Basado en tus búsquedas frecuentes',
          icon: 'Repeat',
          searchParams: { search: combinedQuery, page_size: 10 }
        });
        usedIds.add(id);
      }
    }

    // 5. Second search query as independent section (if different enough)
    if (recentSearches.length >= 2) {
      const secondSearch = recentSearches[1];
      // Only if significantly different from first search
      const firstQuery = (recentSearches[0]?.query || '').toLowerCase();
      const secondQuery = secondSearch.query.toLowerCase();
      if (!firstQuery.includes(secondQuery) && !secondQuery.includes(firstQuery)) {
        const id = `also-searched-${secondQuery.replace(/\s+/g, '-')}`;
        if (!usedIds.has(id)) {
          recommendations.push({
            id, title: `También buscaste "${secondSearch.query}"`,
            subtitle: 'Más resultados de esta búsqueda anterior',
            icon: 'RotateCcw',
            searchParams: { search: secondSearch.query, page_size: 10 }
          });
          usedIds.add(id);
        }
      }
    }

    // 6. By coverage overlap: "Con cobertura en [región]"
    const state = getState();
    const coverageCounts = {};
    for (const view of state.recentViews) {
      for (const cov of (view.coverage || [])) {
        coverageCounts[cov] = (coverageCounts[cov] || 0) + 1;
      }
    }
    // Also extract locations from searches with location-like filters
    for (const s of state.recentSearches) {
      if (s.filters?.coverage) {
        const cov = s.filters.coverage;
        coverageCounts[cov] = (coverageCounts[cov] || 0) + 2;
      }
    }
    const topCoverage = Object.entries(coverageCounts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 1)
      .map(([cov]) => cov);

    if (topCoverage.length > 0) {
      const id = `coverage-${topCoverage[0].replace(/\s+/g, '-').toLowerCase()}`;
      if (!usedIds.has(id)) {
        recommendations.push({
          id, title: `Con cobertura en ${topCoverage[0]}`,
          subtitle: `Proveedores que operan en tu zona de interés`,
          icon: 'Globe',
          searchParams: { search: topCoverage[0], page_size: 10 }
        });
      }
    }

    // 7. From forum/community activity
    if (forumKeywords.length > 0) {
      // Use top 2 forum keywords to build a search
      const topForumTerms = forumKeywords
        .map(k => k.keyword)
        .filter(k => !coveredTerms.has(k))
        .slice(0, 2);

      if (topForumTerms.length > 0) {
        const forumQuery = topForumTerms.join(' ');
        const id = `forum-interest-${forumQuery.replace(/\s+/g, '-')}`;
        if (!usedIds.has(id)) {
          recommendations.push({
            id, title: `De la comunidad: "${topForumTerms[0]}"`,
            subtitle: 'Proveedores relacionados con tus intereses en la red social',
            icon: 'Users',
            searchParams: { search: forumQuery, page_size: 10 }
          });
          usedIds.add(id);
        }
      }
    }

    return recommendations;
  },

  /**
   * Remove a specific search from history
   */
  removeSearch(query) {
    const state = getState();
    state.recentSearches = state.recentSearches.filter(
      s => s.query.toLowerCase() !== query.toLowerCase()
    );
    safeSave(state);
  },

  // ── Forum / Community Interactions ──

  /**
   * Track a forum interaction (like, save, reply, post creation).
   * Extracts category, keywords, and topic signals for recommendations.
   *
   * @param {string} action - 'like' | 'save' | 'reply' | 'post' | 'share'
   * @param {object} post - Forum post object with category_name, technical_description, post_type, item_type
   */
  trackForumInteraction(action, post) {
    if (!post) return;
    const state = getState();

    // Weight: save > like > reply > share > post-view
    const WEIGHTS = { save: 4, like: 3, reply: 2.5, post: 2, share: 1.5 };
    const weight = WEIGHTS[action] || 1;

    // Extract category signal
    const category = post.category_name || post.category || '';
    if (category) {
      state.categoryInteractions.push({
        category,
        action: `forum_${action}`,
        timestamp: now(),
        weight
      });
    }

    // Extract keywords from post title/body for search-like signals
    const rawDesc = post.technical_description || '';
    const parts = rawDesc.split('|||');
    const titleBody = parts[0] || '';
    const [title, body] = titleBody.split('||');
    const fullText = `${title || ''} ${body || ''}`.trim();

    // Save meaningful keywords (4+ chars) as forum-derived search terms
    if (fullText.length > 0) {
      const STOP_WORDS = new Set(['para', 'como', 'esta', 'esto', 'esos', 'esas', 'tiene', 'tienen', 'necesito', 'busco', 'alguien', 'puede', 'donde', 'cual', 'quien', 'todo', 'todos', 'sobre', 'entre', 'desde', 'hasta', 'pero', 'porque', 'cuando', 'también', 'otra', 'otro', 'otros', 'algunas', 'algunos', 'nuevo', 'nueva', 'buenos', 'buenas', 'hacer', 'haber', 'tener', 'empresa', 'empresas', 'proveedor', 'proveedores', 'favor', 'gracias', 'urgente', 'necesita']);
      const words = fullText
        .toLowerCase()
        .replace(/[^a-záéíóúñü\s]/g, '')
        .split(/\s+/)
        .filter(w => w.length >= 4 && !STOP_WORDS.has(w));

      // Store unique keywords from this interaction
      const keywords = [...new Set(words)].slice(0, 8);

      // Store as a forum-derived search signal
      if (!state.forumKeywords) state.forumKeywords = [];
      state.forumKeywords.push({
        keywords,
        action,
        category,
        postType: post.post_type || 'cotizacion',
        itemType: post.item_type || '',
        timestamp: now(),
        weight
      });

      // Keep last 50 forum interactions
      state.forumKeywords = state.forumKeywords.slice(-50);
    }

    // Trim category interactions if needed
    if (state.categoryInteractions.length > MAX_CATEGORY_HISTORY) {
      state.categoryInteractions = state.categoryInteractions.slice(-MAX_CATEGORY_HISTORY);
    }

    safeSave(state);
  },

  /**
   * Get top keywords from forum interactions (likes, saves, replies)
   * weighted by interaction type and recency.
   */
  getForumKeywords(limit = 8) {
    const state = getState();
    if (!state.forumKeywords || state.forumKeywords.length === 0) return [];

    const keywordScores = {};
    for (const interaction of state.forumKeywords) {
      const decay = decayWeight(interaction.timestamp);
      for (const kw of (interaction.keywords || [])) {
        keywordScores[kw] = (keywordScores[kw] || 0) + (interaction.weight * decay);
      }
    }

    return Object.entries(keywordScores)
      .sort(([, a], [, b]) => b - a)
      .slice(0, limit)
      .map(([keyword, score]) => ({ keyword, score }));
  },

  // ── Geolocation ──

  /**
   * Get cached user location (city/region detected via browser geolocation)
   * Returns { city, region, country, lat, lng, timestamp } or null
   */
  getUserLocation() {
    try {
      const raw = localStorage.getItem('pm_user_location');
      if (!raw) return null;
      const loc = JSON.parse(raw);
      // Cache for 24 hours
      if (now() - loc.timestamp > 24 * 60 * 60 * 1000) return null;
      return loc;
    } catch {
      return null;
    }
  },

  /**
   * Save detected user location
   */
  saveUserLocation(locationData) {
    try {
      localStorage.setItem('pm_user_location', JSON.stringify({
        ...locationData,
        timestamp: now()
      }));
    } catch {
      // Silently fail
    }
  },

  /**
   * Detect user location via browser Geolocation API + reverse geocoding.
   * Returns { city, region, country, lat, lng } or null.
   * Caches result for 24h.
   */
  async detectLocation() {
    // Return cached if fresh
    const cached = this.getUserLocation();
    if (cached) return cached;

    // Check if geolocation is available
    if (!navigator.geolocation) return null;

    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: false,
          timeout: 8000,
          maximumAge: 3600000 // 1 hour
        });
      });

      const { latitude, longitude } = position.coords;

      // Reverse geocode via Nominatim (OpenStreetMap, free, no API key)
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json&accept-language=es`,
        { headers: { 'User-Agent': 'ProveMiner/1.0' } }
      );

      if (!response.ok) return null;
      const data = await response.json();
      const address = data.address || {};

      const locationData = {
        city: address.city || address.town || address.village || address.municipality || '',
        region: address.state || address.county || '',
        country: address.country || '',
        lat: latitude,
        lng: longitude
      };

      // Only save if we got a meaningful city
      if (locationData.city || locationData.region) {
        this.saveUserLocation(locationData);
        return locationData;
      }

      return null;
    } catch {
      // User denied permission or network error — fail silently
      return null;
    }
  }
};

export default preferenceEngine;
